"use client";

import { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import {
    Shield, CreditCard, ClipboardCheck, Truck, Wrench,
    Store, Navigation as NavIcon, Search, MapPin,
    ExternalLink, PhoneCall, Star, Loader2, AlertCircle
} from 'lucide-react';
import { servicesApi, AutomotiveService } from '@/lib/api/services';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const SERVICE_TYPES = [
    { id: '', label: 'الكل', icon: Store },
    { id: 'insurance', label: 'تأمين', icon: Shield },
    { id: 'financing', label: 'تمويل', icon: CreditCard },
    { id: 'inspection', label: 'فحص', icon: ClipboardCheck },
    { id: 'towing', label: 'سحب (ونش)', icon: Truck },
    { id: 'mobile_workshop', label: 'ورشة متنقلة', icon: Wrench },
    { id: 'roadside', label: 'مساعدة على الطريق', icon: NavIcon },
];

export default function ServicesPage() {
    const [services, setServices] = useState<AutomotiveService[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    // Geolocation state
    const [userLocation, setUserLocation] = useState<{ lat: number, lng: number } | null>(null);
    const [locating, setLocating] = useState(false);
    const [locationError, setLocationError] = useState('');

    // Assistance Request Modal
    const [selectedService, setSelectedService] = useState<AutomotiveService | null>(null);
    const [requestNotes, setRequestNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const { toast } = useToast();

    const fetchServices = async () => {
        setLoading(true);
        try {
            const filters: any = {};
            if (filterType) filters.service_type = filterType;
            if (searchQuery) filters.search = searchQuery;

            // If we have location, request sorted by distance
            if (userLocation) {
                filters.lat = userLocation.lat;
                filters.lng = userLocation.lng;
                filters.ordering = 'distance';
            }

            const data = await servicesApi.getServices(filters);
            setServices(data.results || data || []);
        } catch (error) {
            toast({ title: 'خطأ', description: 'فشل في تحميل الخدمات', variant: 'destructive' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchServices();
    }, [filterType, searchQuery, userLocation]);

    const requestLocation = () => {
        setLocating(true);
        setLocationError('');
        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setUserLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    });
                    setLocating(false);
                    toast({ title: 'تم تحديد موقعك', description: 'يتم الآن عرض أقرب الخدمات لك' });
                },
                (error) => {
                    setLocating(false);
                    setLocationError('تعذر تحديد موقعك. يرجى تفعيل إذن الوصول للموقع.');
                    toast({ title: 'تنبيه', description: 'تعذر تحديد الموقع', variant: 'destructive' });
                }
            );
        } else {
            setLocating(false);
            setLocationError('المتصفح لا يدعم تحديد الموقع');
        }
    };

    const handleRequestAssistance = async () => {
        if (!selectedService) return;

        setSubmitting(true);
        try {
            await servicesApi.createRequest({
                service: selectedService.id,
                user_latitude: userLocation?.lat,
                user_longitude: userLocation?.lng,
                notes: requestNotes
            });

            toast({
                title: '✅ تم إرسال طلبك بنجاح',
                description: 'سيقوم مقدم الخدمة بالتواصل معك قريباً'
            });
            setSelectedService(null);
            setRequestNotes('');
        } catch (error) {
            toast({ title: 'خطأ', description: 'فشل إرسال الطلب', variant: 'destructive' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col font-sans" dir="rtl">
            <Navigation title="الخدمات المتكاملة" />

            {/* Hero Section */}
            <div className="bg-gradient-to-r from-slate-900 to-blue-900 text-white py-16 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10 mix-blend-overlay"></div>
                <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
                    <Badge className="bg-blue-500/20 text-blue-200 border-none mb-4 backdrop-blur-md">الخدمات المتكاملة</Badge>
                    <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight">دليلك الشامل لخدمات السيارات</h1>
                    <p className="text-slate-300 text-lg max-w-2xl mx-auto">
                        تأمين، تمويل، ورش متنقلة، ومساعدة على الطريق. اكتشف أقرب مزودي الخدمات المعتمدين واطلب المساعدة بضغطة زر.
                    </p>

                    <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 max-w-2xl mx-auto">
                        <div className="relative w-full">
                            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                            <Input
                                placeholder="ابحث عن شركات التأمين، الورش، خدمات السحب..."
                                className="w-full h-14 pr-12 rounded-full bg-white/10 text-white border-white/20 placeholder:text-slate-400 focus-visible:ring-blue-500"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <Button
                            onClick={requestLocation}
                            disabled={locating}
                            className={`h-14 rounded-full px-6 gap-2 whitespace-nowrap transition-all ${userLocation ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-primary hover:bg-primary/90'}`}
                        >
                            {locating ? <Loader2 className="w-5 h-5 animate-spin" /> : <MapPin className="w-5 h-5" />}
                            {userLocation ? 'موقعك محدّد' : 'استخدام موقعي الحالي'}
                        </Button>
                    </div>
                    {locationError && <p className="text-amber-400 text-sm mt-3 flex items-center justify-center gap-2"><AlertCircle className="w-4 h-4" /> {locationError}</p>}
                </div>
            </div>

            <div className="flex-grow max-w-7xl mx-auto w-full px-4 py-8">

                {/* Categories Bar */}
                <div className="flex gap-2 overflow-x-auto pb-4 scrollbar-hide">
                    {SERVICE_TYPES.map(type => (
                        <button
                            key={type.id}
                            onClick={() => setFilterType(type.id)}
                            className={`flex items-center gap-2 px-5 py-3 rounded-full whitespace-nowrap text-sm font-bold transition-all shadow-sm
                ${filterType === type.id
                                    ? 'bg-primary text-white scale-105'
                                    : 'bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900 border border-slate-200'}`}
                        >
                            <type.icon className="w-4 h-4" />
                            {type.label}
                        </button>
                    ))}
                </div>

                {/* Services Grid */}
                <div className="mt-8">
                    {loading ? (
                        <div className="flex justify-center py-20">
                            <Loader2 className="w-12 h-12 animate-spin text-primary" />
                        </div>
                    ) : services.length === 0 ? (
                        <div className="text-center py-20 bg-white rounded-3xl border border-slate-100 shadow-sm">
                            <Store className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                            <h2 className="text-xl font-bold text-slate-800">لا توجد خدمات متاحة</h2>
                            <p className="text-slate-500 mt-2">جرب البحث بكلمات أخرى أو اختر فئة مختلفة.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {services.map(service => {
                                const isLocationService = ['towing', 'mobile_workshop', 'roadside'].includes(service.service_type);

                                return (
                                    <Card key={service.id} className="rounded-3xl border-slate-100 shadow-xl shadow-slate-200/50 hover:-translate-y-1 transition-transform overflow-hidden flex flex-col">
                                        <CardContent className="p-0 flex flex-col h-full">
                                            <div className="p-6 bg-gradient-to-br from-white to-slate-50 border-b border-slate-100 relative">
                                                {service.is_featured && (
                                                    <div className="absolute top-4 left-4 bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-sm">
                                                        <Star className="w-3 h-3 fill-amber-500" /> مميز
                                                    </div>
                                                )}
                                                <div className="flex items-center gap-4 mb-4">
                                                    <div className="w-16 h-16 rounded-2xl bg-white shadow-sm border border-slate-100 flex items-center justify-center p-2 overflow-hidden">
                                                        {service.logo ? (
                                                            <img src={service.logo} alt={service.name} className="w-full h-full object-contain" />
                                                        ) : (
                                                            <Store className="w-8 h-8 text-primary/40" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <h3 className="font-bold text-lg text-slate-800 line-clamp-1">{service.name}</h3>
                                                        <Badge variant="outline" className="mt-1 bg-white text-slate-500 border-slate-200">
                                                            {SERVICE_TYPES.find(t => t.id === service.service_type)?.label || service.service_type}
                                                        </Badge>
                                                    </div>
                                                </div>
                                                <p className="text-sm text-slate-600 line-clamp-2 min-h-[40px]">
                                                    {service.short_description || 'مزود خدمة معتمد في المنصة.'}
                                                </p>
                                            </div>

                                            <div className="p-6 bg-white flex flex-col flex-1 justify-between gap-4">
                                                <div className="space-y-3 text-sm flex-1">
                                                    {service.contact_phone && (
                                                        <div className="flex items-center gap-3 text-slate-700">
                                                            <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 shrink-0">
                                                                <PhoneCall className="w-4 h-4" />
                                                            </div>
                                                            <span dir="ltr" className="select-all font-medium text-right w-full">{service.contact_phone}</span>
                                                        </div>
                                                    )}

                                                    {(service.address || service.distance_km !== undefined) && (
                                                        <div className="flex items-start gap-3 text-slate-700">
                                                            <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 shrink-0 mt-0.5">
                                                                <MapPin className="w-4 h-4" />
                                                            </div>
                                                            <div className="flex-1">
                                                                {service.address && <span className="block line-clamp-1">{service.address}</span>}
                                                                {service.distance_km !== undefined && (
                                                                    <span className={`text-xs font-bold block ${service.address ? 'mt-1' : ''} ${service.distance_km < 10 ? 'text-emerald-600' : 'text-slate-500'}`}>
                                                                        يبعد عنك: {service.distance_km < 1 ? 'أقل من 1 كم' : `${service.distance_km.toFixed(1)} كم`}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="grid grid-cols-2 gap-3 mt-4 w-full">
                                                    {service.website_url ? (
                                                        <a href={service.website_url} target="_blank" rel="noopener noreferrer" className="col-span-2 sm:col-span-1">
                                                            <Button variant="outline" className="w-full rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 h-10 gap-2">
                                                                <ExternalLink className="w-4 h-4" /> زيارة الموقع
                                                            </Button>
                                                        </a>
                                                    ) : (
                                                        <div className="col-span-2 sm:col-span-1" />
                                                    )}

                                                    {isLocationService ? (
                                                        <Button
                                                            onClick={() => setSelectedService(service)}
                                                            className={`w-full rounded-xl h-10 gap-2 ${service.website_url ? 'col-span-2 sm:col-span-1' : 'col-span-2'} bg-primary hover:bg-primary/90 text-white`}
                                                        >
                                                            <NavIcon className="w-4 h-4" /> طلب مساعدة
                                                        </Button>
                                                    ) : (
                                                        !service.website_url && (
                                                            <Button variant="outline" className="w-full col-span-2 rounded-xl border-blue-100 text-blue-600 bg-blue-50 hover:bg-blue-100 h-10 gap-2 font-bold select-none cursor-default">
                                                                <PhoneCall className="w-4 h-4" /> تواصل الآن
                                                            </Button>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Assistance Request Modal */}
            <Dialog open={!!selectedService} onOpenChange={(open) => !open && setSelectedService(null)}>
                <DialogContent className="sm:max-w-md bg-white rounded-3xl border-0 shadow-2xl" dir="rtl">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-black text-slate-800 flex items-center gap-3">
                            <div className="p-2 bg-primary/10 text-primary rounded-xl">
                                <NavIcon className="w-5 h-5" />
                            </div>
                            طلب المساعدة
                        </DialogTitle>
                        <DialogDescription className="text-slate-500 pt-2">
                            سيتم إرسال موقعك الحالي مع الطلب إلى <strong className="text-slate-800">{selectedService?.name}</strong> لتسهيل الوصول إليك.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="py-4 space-y-4">
                        {!userLocation && (
                            <div className="bg-amber-50 rounded-xl p-4 border border-amber-100 flex gap-3 text-amber-800 text-sm">
                                <AlertCircle className="w-5 h-5 shrink-0" />
                                <p>موقعك غير محدد. ننصح بمشاركة موقعك (Location) لتسريع وصول مزود الخدمة، أو يمكنك وصف موقعك في الملاحظات.</p>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="notes" className="text-slate-700 font-bold">ملاحظات والتفاصيل</Label>
                            <Textarea
                                id="notes"
                                placeholder="اكتب أي معلومات إضافية (مثل: السيارة معطلة في المواقف السفلية، رقم اللوحة...)"
                                className="resize-none h-24 rounded-xl border-slate-200 focus-visible:ring-primary/20 focus-visible:border-primary bg-slate-50/50"
                                value={requestNotes}
                                onChange={(e) => setRequestNotes(e.target.value)}
                            />
                        </div>
                    </div>

                    <DialogFooter className="flex-col sm:flex-row gap-3 mt-2 sm:justify-start pt-2 border-t border-slate-100">
                        <Button
                            type="submit"
                            onClick={handleRequestAssistance}
                            disabled={submitting}
                            className="w-full sm:w-auto rounded-xl bg-primary hover:bg-primary/90 text-white h-12 px-8 font-bold text-base"
                        >
                            {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : 'تأكيد إرسال الطلب'}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setSelectedService(null)}
                            className="w-full sm:w-auto rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 h-12 px-6"
                        >
                            إلغاء
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
